import { getWebaliasAlongWithRestPath } from '@/helpers/utils/urlHelpers';

import {
  AUTH_REFRESH_URL,
  AUTH_URL,
  IS_DEV,
  IS_LOCAL,
  IS_LOCAL_INTEGRATED_MODE,
  IS_LOCAL_MODE,
  IS_STAGE,
  SERVER_URL,
  STRAPI_SERVER_URL,
} from './env';

type ENVS = 'local-dev' | 'local-stg' | 'dev' | 'stage' | 'production';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getClientEnv() {
  const host = globalThis?.window?.location?.host;
  const origin = globalThis?.window?.location?.origin;
  const { webalias: raw_webalias, urlPath } = getWebaliasAlongWithRestPath(
    globalThis?.window?.location?.pathname || ''
  );

  const webalias = raw_webalias?.split('?')[0];

  const REPLICATED_URL = IS_LOCAL
    ? 'https://local.pawtreedev.com'
    : IS_DEV
    ? `https://v2.pawtreedev.com`
    : IS_STAGE
    ? 'https://v2-stg.pawtree.com'
    : 'https://pawtree.com';

  const isBackOffice = !!host?.includes('backoffice');

  const WEBALIAS_PATH = `/${webalias || 'pawtree'}`;

  const BACKOFFICE_ORIGIN = IS_LOCAL
    ? 'https://backoffice-local.pawtreedev.com'
    : IS_DEV
    ? 'https://backoffice-v2.pawtreedev.com'
    : IS_STAGE
    ? 'https://backoffice-v2-stg.pawtree.com'
    : 'https://backoffice.pawtree.com';

  const BACKOFFICE_URL = isBackOffice
    ? IS_LOCAL
      ? 'https://backoffice-local.pawtreedev.com'
      : IS_DEV
      ? 'https://backoffice-v2.pawtreedev.com'
      : IS_STAGE
      ? 'https://backoffice-v2-stg.pawtree.com'
      : 'https://backoffice.pawtree.com'
    : IS_LOCAL || IS_DEV
    ? 'https://v2.pawtreedev.com'
    : IS_STAGE
    ? 'https://v2-stg.pawtree.com'
    : 'https://pawtree.com';

  const FINAL_SERVER_URL = IS_LOCAL_MODE ? SERVER_URL : `${origin}/internal_api/api`;
  const FINAL_AUTH_URL = IS_LOCAL_MODE
    ? AUTH_URL
    : `${origin}${isBackOffice ? '' : WEBALIAS_PATH}/account/checkifuserauthenticated`;
  const FINAL_AUTH_REFRESH_URL = IS_LOCAL_MODE
    ? AUTH_REFRESH_URL
    : `${origin}${isBackOffice ? '' : WEBALIAS_PATH}/account/pingtest`;

  // Apple Sign-In redirect URLs
  const APPLE_REDIRECT_URL_BACKOFFICE = `${BACKOFFICE_ORIGIN}/login`;
  const APPLE_REDIRECT_URL_REPLICATED = `${REPLICATED_URL}/pawtree/account/login`;

  return {
    // HOST: host,
    IS_LOCAL_MODE,
    // ORIGIN: origin,
    STRAPI_SERVER_URL,
    WEBALIAS: webalias,
    URL_PATH_WITHOUT_WEBALIAS: urlPath,
    IS_LOCAL_INTEGRATED_MODE,
    AUTH_URL: FINAL_AUTH_URL || '',
    IS_BACKOFFICE: isBackOffice,
    SERVER_URL: FINAL_SERVER_URL || '',
    AUTH_REFRESH_URL: FINAL_AUTH_REFRESH_URL || '',
    REPLICATED_URL,
    BACKOFFICE_URL,
    BACKOFFICE_ORIGIN,
    APPLE_REDIRECT_URL_BACKOFFICE,
    APPLE_REDIRECT_URL_REPLICATED,
    generateAuthURL: (WEBALIAS_PATH?: `/${string}`) => {
      return `${origin}/internal_api/api/authentication/GetAuthStatus`;
    },

    generateAuthRefreshURL: (WEBALIAS_PATH?: `/${string}`) => {
      return `${origin}/internal_api/api/Authentication/refresh`;
    },

    get HOST() {
      return globalThis?.window?.location?.host || host;
    },
    get ORIGIN() {
      return globalThis?.window?.location?.origin || origin;
    },

    get CURRENT_ENV(): ENVS {
      if (
        globalThis?.window?.location?.host.includes('local') &&
        globalThis?.window?.location?.host.includes('pawtreedev')
      )
        return 'local-dev';
      if (
        globalThis?.window?.location?.host.includes('local') &&
        globalThis?.window?.location?.host.includes('pawtree')
      )
        return 'local-stg';
      if (globalThis?.window?.location?.host.includes('dev')) return 'dev';
      if (globalThis?.window?.location?.host.includes('stg')) return 'stage';
      return 'production';
    },

    getSibilingEnvs: (ENV: ENVS) => {
      const localDevHostnames = ['backoffice-local.pawtreedev.com', 'local.pawtreedev.com'];
      const localStgHostnames = ['backoffice-local.pawtree.com', 'local.pawtree.com'];
      const devHostnames = ['backoffice-v2.pawtreedev.com', 'v2.pawtreedev.com'];
      const stageHostnames = ['backoffice-v2-stg.pawtree.com', 'shop-v2-stg.pawtree.com', 'v2-stg.pawtree.com'];
      const prodHostnames = ['backoffice.pawtree.com', 'pawtree.com', 'shop.pawtree.com'];

      let returnable: string[] = [];

      if (ENV === 'local-dev') {
        returnable = localDevHostnames;
      } else if (ENV === 'local-stg') {
        returnable = localStgHostnames;
      } else if (ENV === 'dev') {
        returnable = devHostnames;
      } else if (ENV === 'stage') {
        returnable = stageHostnames;
      } else if (ENV === 'production') {
        returnable = prodHostnames;
      }

      console.log('DEBUG: Sibling envs for', ENV, 'are', returnable);

      return returnable.filter(h => h !== globalThis?.window?.location?.host);
    },

    checkIfRootDomain: (host: string, env: ENVS) => {
      const devHost: string = '.pawtreedev.com';
      const prodHost: string = '.pawtree.com';

      switch (env) {
        case 'dev':
        case 'local-dev':
          return host === devHost;

        case 'stage':
        case 'local-stg':
        case 'production':
          return host === prodHost;

        default:
          return false;
      }
    },
  };
}
